import { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/lib/providers';
import { useToast } from '@/components/ui/use-toast';
import { Check, X } from 'lucide-react';
import type { Database } from '@/types/supabase';

type ChatSource = Database['public']['Enums']['chat_source'];
type ReportStatus = Database['public']['Enums']['chat_report_status'];

interface ReportRow {
  id: string;
  reporter_id: string;
  reported_user_id: string;
  message_source: ChatSource;
  message_id: string;
  reason: string | null;
  status: ReportStatus;
  created_at: string;
  reporter_name: string;
  reported_name: string;
  message_content: string | null;
}

const STATUS_LABEL: Record<ReportStatus, string> = {
  open: 'Open',
  resolved: 'Afgehandeld',
  dismissed: 'Genegeerd',
};

export default function AdminModerationPage() {
  const { profile } = useProfile();
  const { toast } = useToast();
  const [tab, setTab] = useState<ReportStatus>('open');
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('chat_reports')
      .select('*')
      .eq('status', tab)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Failed to load reports:', error);
      setReports([]);
      setLoading(false);
      return;
    }

    const rows = data ?? [];
    const userIds = Array.from(
      new Set(rows.flatMap((r) => [r.reporter_id, r.reported_user_id])),
    );
    const samenIds = rows.filter((r) => r.message_source === 'samen').map((r) => r.message_id);
    const teamIds = rows.filter((r) => r.message_source === 'team').map((r) => r.message_id);

    const [profilesRes, samenRes, teamRes] = await Promise.all([
      userIds.length
        ? supabase.from('profiles').select('id, naam').in('id', userIds)
        : Promise.resolve({ data: [], error: null }),
      samenIds.length
        ? supabase.from('samen_messages').select('id, content').in('id', samenIds)
        : Promise.resolve({ data: [], error: null }),
      teamIds.length
        ? supabase.from('team_messages').select('id, content').in('id', teamIds)
        : Promise.resolve({ data: [], error: null }),
    ]);

    const nameById = new Map(
      (profilesRes.data ?? []).map((p: any) => [p.id, p.naam ?? 'Onbekend']),
    );
    const contentById = new Map<string, string>();
    for (const m of samenRes.data ?? []) contentById.set((m as any).id, (m as any).content);
    for (const m of teamRes.data ?? []) contentById.set((m as any).id, (m as any).content);

    setReports(
      rows.map((r) => ({
        ...r,
        reporter_name: nameById.get(r.reporter_id) ?? 'Onbekend',
        reported_name: nameById.get(r.reported_user_id) ?? 'Onbekend',
        message_content: contentById.get(r.message_id) ?? null,
      })),
    );
    setLoading(false);
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (id: string, status: ReportStatus) => {
    if (!profile?.id) return;
    const { error } = await supabase
      .from('chat_reports')
      .update({
        status,
        resolved_at: new Date().toISOString(),
        resolved_by: profile.id,
      })
      .eq('id', id);
    if (error) {
      console.error('Failed to update report:', error);
      toast({
        variant: 'destructive',
        title: 'Bijwerken mislukt',
        description: error.message,
      });
      return;
    }
    setReports((r) => r.filter((row) => row.id !== id));
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Moderatie</h1>

      <Tabs value={tab} onValueChange={(v) => setTab(v as ReportStatus)}>
        <TabsList>
          <TabsTrigger value="open">Open</TabsTrigger>
          <TabsTrigger value="resolved">Afgehandeld</TabsTrigger>
          <TabsTrigger value="dismissed">Genegeerd</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4 space-y-3">
          {loading ? (
            <p className="text-muted-foreground">Laden...</p>
          ) : reports.length === 0 ? (
            <p className="text-muted-foreground">Geen meldingen in deze categorie.</p>
          ) : (
            reports.map((r) => (
              <Card key={r.id} className="p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge variant="outline">
                    {r.message_source === 'samen' ? 'Samen' : 'Team'}
                  </Badge>
                  <Badge>{STATUS_LABEL[r.status]}</Badge>
                  <span className="text-muted-foreground">
                    {new Date(r.created_at).toLocaleString('nl-NL')}
                  </span>
                </div>
                <div className="text-sm">
                  <div>
                    <span className="font-medium">{r.reporter_name}</span>{' '}
                    rapporteerde{' '}
                    <span className="font-medium">{r.reported_name}</span>
                  </div>
                  {r.reason && (
                    <div className="mt-1 text-muted-foreground italic">
                      Reden: {r.reason}
                    </div>
                  )}
                  <div className="mt-2 p-2 rounded bg-muted/50 border-l-2 border-red-500">
                    {r.message_content ?? (
                      <span className="text-muted-foreground italic">
                        Bericht is verwijderd
                      </span>
                    )}
                  </div>
                </div>
                {r.status === 'open' && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => resolve(r.id, 'resolved')}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Check className="mr-1 h-4 w-4" />
                      Afgehandeld
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => resolve(r.id, 'dismissed')}
                    >
                      <X className="mr-1 h-4 w-4" />
                      Negeer
                    </Button>
                  </div>
                )}
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
